import { useState, useEffect, useRef } from "react";
import { FaHandPaper, FaArrowRight, FaTrophy, FaCoins, FaGift, FaBolt, FaTimes, FaExclamationTriangle, FaLock, FaQuestionCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
// SendSlapPage is now a standalone page - navigation handled via router
import TransactionFeedback from "./TransactionFeedback";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { useRadixConnect } from "../../../radix_connect";
import {
  RADIX_API_GATEWAY_URL,
  DCKSLAP_RESOURCE_ADDRESS,
  DCKSLAP_USER_BADGE_RESOURCE_ADDRESS,
  DCKSLAP_COMPONENT_ADDRESS,
  REDDICKS_RESSOURCE_ADDRESS,
  DCKSLAP_DAILY_WITHDRAWAL_LIMIT,
} from "../../../constants";
import "./SlapGenerator.css";

const SLAP_STORAGE_KEY_PREFIX = "slapsRemaining";
const SLAP_TIME_STORAGE_KEY_PREFIX = "lastSlapTime";
const DAILY_SLAP_RESET_MS = 24 * 60 * 60 * 1000;

const getWalletScopedStorageKey = (prefix, accountAddress) =>
  `${prefix}:${accountAddress}`;

const getStoredSlapState = (accountAddress) => {
  if (!accountAddress) {
    return DCKSLAP_DAILY_WITHDRAWAL_LIMIT;
  }

  const slapsKey = getWalletScopedStorageKey(
    SLAP_STORAGE_KEY_PREFIX,
    accountAddress
  );
  const lastSlapTimeKey = getWalletScopedStorageKey(
    SLAP_TIME_STORAGE_KEY_PREFIX,
    accountAddress
  );
  const storedSlaps = localStorage.getItem(slapsKey);
  const lastSlapTime = localStorage.getItem(lastSlapTimeKey);

  if (storedSlaps !== null && lastSlapTime !== null) {
    const parsedLastSlapTime = parseInt(lastSlapTime, 10);

    if (
      Number.isNaN(parsedLastSlapTime) ||
      Date.now() - parsedLastSlapTime > DAILY_SLAP_RESET_MS
    ) {
      localStorage.setItem(
        slapsKey,
        DCKSLAP_DAILY_WITHDRAWAL_LIMIT.toString()
      );
      return DCKSLAP_DAILY_WITHDRAWAL_LIMIT;
    }

    const parsedStoredSlaps = parseInt(storedSlaps, 10);
    if (Number.isFinite(parsedStoredSlaps)) {
      const normalizedStoredSlaps = Math.min(
        Math.max(parsedStoredSlaps, 0),
        DCKSLAP_DAILY_WITHDRAWAL_LIMIT
      );
      localStorage.setItem(slapsKey, normalizedStoredSlaps.toString());
      return normalizedStoredSlaps;
    }
  }

  localStorage.setItem(slapsKey, DCKSLAP_DAILY_WITHDRAWAL_LIMIT.toString());
  return DCKSLAP_DAILY_WITHDRAWAL_LIMIT;
};

const persistSlapState = (accountAddress, slapsRemaining) => {
  if (!accountAddress) {
    return;
  }

  localStorage.setItem(
    getWalletScopedStorageKey(SLAP_STORAGE_KEY_PREFIX, accountAddress),
    slapsRemaining.toString()
  );
  localStorage.setItem(
    getWalletScopedStorageKey(SLAP_TIME_STORAGE_KEY_PREFIX, accountAddress),
    Date.now().toString()
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SLAP VAULT COMPONENT - Premium gamified vault buttons
// ═══════════════════════════════════════════════════════════════════════════════
const SlapVault = ({
  withdrawTitle,
  depositTitle,
  withdrawInstruction,
  depositInstruction,
  status,
  depositStatus,
  onWithdraw,
  onDeposit,
  isWithdrawDisabled,
  isDepositDisabled,
}) => {
  return (
    <div className="grid md:grid-cols-2 gap-6 w-full">
      {/* Withdraw Section */}
      <div className="flex flex-col items-center text-center">
        <h3 className="text-xl md:text-2xl font-semibold text-primary mb-3">
          {withdrawTitle}
        </h3>
        <p
          className="text-sm text-muted-foreground mb-6 max-w-[280px] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: withdrawInstruction }}
        />

        {/* Premium Vault Button - Withdraw */}
        <button
          className={`
            vault-button withdraw
            ${status === "charging" ? "charging" : ""}
            ${isWithdrawDisabled ? "" : ""}
          `}
          onClick={!isWithdrawDisabled && status !== "charging" ? onWithdraw : null}
          disabled={isWithdrawDisabled || status === "charging"}
          aria-label={
            status === "charging"
              ? "Withdrawing your DCKSLAP, please wait"
              : !isWithdrawDisabled
                ? "Click to withdraw a DCKSLAP"
                : "No slaps remaining today"
          }
          aria-disabled={isWithdrawDisabled}
        >
          {/* Animated ring effect */}
          {status === "charging" && (
            <div className="vault-ping withdraw" />
          )}

          {/* Inner glow effect */}
          <div
            className={`
              vault-inner-glow withdraw
              ${status === "charging" ? "opacity-100 animate-pulse" : "opacity-0 group-hover:opacity-100"}
            `}
          />

          {/* Core content */}
          <div className="relative z-10 flex flex-col items-center">
            <FaHandPaper
              className={`
                text-4xl md:text-5xl text-foreground mb-2
                transition-transform duration-300
                ${status === "charging" ? "animate-bounce" : "group-hover:scale-110"}
              `}
            />
            <span className="text-sm md:text-base font-semibold text-foreground">
              {status === "charging" ? "Withdrawing..." : "Withdraw"}
            </span>
          </div>
        </button>

        {status === "charging" && (
          <p className="mt-4 text-sm text-primary charging-text font-medium">
            Getting a big slap...
          </p>
        )}
      </div>

      {/* Deposit Section */}
      <div className="flex flex-col items-center text-center">
        <h3 className="text-xl md:text-2xl font-semibold deposit-title mb-3">
          {depositTitle}
        </h3>
        <p
          className="text-sm text-muted-foreground mb-6 max-w-[280px] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: depositInstruction }}
        />

        {/* Premium Vault Button - Deposit */}
        <button
          className={`
            vault-button deposit
            ${depositStatus === "charging" ? "charging" : ""}
          `}
          onClick={!isDepositDisabled && depositStatus !== "charging" ? onDeposit : null}
          disabled={isDepositDisabled || depositStatus === "charging"}
          aria-label={
            depositStatus === "charging"
              ? "Depositing your DCKSLAP, please wait"
              : !isDepositDisabled
                ? "Click to deposit a DCKSLAP"
                : "No slaps available to deposit"
          }
          aria-disabled={isDepositDisabled}
        >
          {/* Animated ring effect */}
          {depositStatus === "charging" && (
            <div className="vault-ping deposit" />
          )}

          {/* Inner glow effect */}
          <div
            className={`
              vault-inner-glow deposit
              ${depositStatus === "charging" ? "opacity-100 animate-pulse" : "opacity-0 group-hover:opacity-100"}
            `}
          />

          {/* Core content */}
          <div className="relative z-10 flex flex-col items-center">
            <FaCoins
              className={`
                text-4xl md:text-5xl text-foreground mb-2
                transition-transform duration-300
                ${depositStatus === "charging" ? "animate-bounce" : "group-hover:scale-110"}
              `}
            />
            <span className="text-sm md:text-base font-semibold text-foreground">
              {depositStatus === "charging" ? "Depositing..." : "Deposit"}
            </span>
          </div>
        </button>

        {depositStatus === "charging" && (
          <p className="mt-4 text-sm charging-text font-medium deposit-charging-text">
            Depositing to earn GBOFs...
          </p>
        )}
      </div>
    </div>
  );
};



// ═══════════════════════════════════════════════════════════════════════════════
// MAIN SLAP GENERATOR COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const SlapGenerator = () => {
  const { rdt, connected, selectedAccount } = useRadixConnect();
  const navigate = useNavigate();
  const accountAddress = selectedAccount?.address;
  const activeAccountAddressRef = useRef(accountAddress);
  const [status, setStatus] = useState("ready");
  const [depositStatus, setDepositStatus] = useState("ready");
  const [slapsRemaining, setSlapsRemaining] = useState(DCKSLAP_DAILY_WITHDRAWAL_LIMIT);
  const [slapsOwned, setSlapsOwned] = useState(0);
  const [userBadges, setUserBadges] = useState([]);
  const [showFlash, setShowFlash] = useState(false);
  const [showShockwave, setShowShockwave] = useState(false);
  const [depositFlash, setDepositFlash] = useState(false);
  const [depositShockwave, setDepositShockwave] = useState(false);
  // Modal state removed - now using dedicated SendSlapPage
  const [hasRedicks, setHasRedicks] = useState(false);
  const [depositCount, setDepositCount] = useState(0);
  const [showTransactionFeedback, setShowTransactionFeedback] = useState(false);
  const [transactionFeedbackVariant, setTransactionFeedbackVariant] = useState("mint");
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'NO_DCKSLAP' | 'NO_BADGE' | 'NO_REDDICKS'

  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS - Data fetching and state management (unchanged logic)
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    activeAccountAddressRef.current = accountAddress;
    setStatus("ready");
    setDepositStatus("ready");
    setShowTransactionFeedback(false);
    setShowFlash(false);
    setShowShockwave(false);
    setDepositFlash(false);
    setDepositShockwave(false);
    setShowInsufficientModal(false);
    setModalType(null);
    setSlapsRemaining(getStoredSlapState(accountAddress));
  }, [accountAddress]);

  useEffect(() => {
    if (!connected || !selectedAccount) {
      setSlapsOwned(0);
      setUserBadges([]);
      setHasRedicks(false);
      return undefined;
    }

    const requestedAccountAddress = selectedAccount.address;
    let isCancelled = false;

    setSlapsOwned(0);
    setUserBadges([]);
    setHasRedicks(false);

    const isActiveRequest = () =>
      !isCancelled &&
      activeAccountAddressRef.current === requestedAccountAddress;

    // Fetch DCKSLAP fungible balance using paginated endpoint
    const fetchDckslapBalance = async () => {
      let dckslapBalance = 0;
      let cursor = null;
      let found = false;
      let state_version = null;

      try {
        do {
          const requestBody = {
            address: requestedAccountAddress,
          };
          if (cursor) {
            requestBody.cursor = cursor;
            requestBody.at_ledger_state = { state_version };
          }

          const response = await fetch(
            RADIX_API_GATEWAY_URL + "/state/entity/page/fungibles/",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(requestBody),
            }
          );
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          const data = await response.json();
          const items = data.items || [];

          for (const item of items) {
            if (item.resource_address === DCKSLAP_RESOURCE_ADDRESS) {
              dckslapBalance = parseFloat(item.amount) || 0;
              found = true;
              break;
            }
          }

          cursor = data.next_cursor || null;
          state_version = data.ledger_state.state_version;
        } while (cursor && !found && !isCancelled);

        if (!isActiveRequest()) {
          return;
        }

        setSlapsOwned(Math.floor(dckslapBalance));
      } catch (error) {
        if (!isActiveRequest()) {
          return;
        }

        console.error("Failed to fetch DCKSLAP balance:", error);
        setSlapsOwned(0);
      }
    };

    // Fetch user badges (non-fungibles) using paginated endpoint
    const fetchUserBadges = async () => {
      let badgeFound = false;
      let cursor = null;
      let state_version = null;

      try {
        // Step 1: Find the badge resource in paginated non-fungibles
        do {
          const requestBody = {
            address: requestedAccountAddress,
          };
          if (cursor) {
            requestBody.cursor = cursor;
            requestBody.at_ledger_state = { state_version };
          }

          const response = await fetch(
            RADIX_API_GATEWAY_URL + "/state/entity/page/non-fungibles/",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(requestBody),
            }
          );
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          const data = await response.json();
          const items = data.items || [];

          for (const item of items) {
            if (
              item.resource_address === DCKSLAP_USER_BADGE_RESOURCE_ADDRESS &&
              item.amount > 0
            ) {
              badgeFound = true;
              break;
            }
          }

          cursor = data.next_cursor || null;
          state_version = data.ledger_state.state_version;
        } while (cursor && !badgeFound && !isCancelled);

        if (!isActiveRequest()) {
          return;
        }

        if (!badgeFound) {
          setUserBadges([]);
          return;
        }

        // Step 2: Fetch NFT IDs from vaults
        let allNfIds = [];
        cursor = null;

        do {
          const requestBody = {
            address: requestedAccountAddress,
            resource_address: DCKSLAP_USER_BADGE_RESOURCE_ADDRESS,
            opt_ins: {
              non_fungible_include_nfids: true,
            },
          };
          if (cursor) {
            requestBody.cursor = cursor;
          }

          const response = await fetch(
            RADIX_API_GATEWAY_URL + "/state/entity/page/non-fungible-vaults/",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(requestBody),
            }
          );
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          const data = await response.json();
          const vaultItems = data.items || [];

          for (const vault of vaultItems) {
            if (vault.items && vault.items.length > 0) {
              allNfIds = allNfIds.concat(vault.items);
            }
          }

          cursor = data.next_cursor || null;
        } while (cursor && !isCancelled);

        if (!isActiveRequest()) {
          return;
        }

        const extractedIds = allNfIds
          .map((idStr) => {
            const match = idStr.match(/#(\d+)#/);
            return match ? match[1] : null;
          })
          .filter((id) => id !== null);
        setUserBadges(extractedIds);
      } catch (error) {
        if (!isActiveRequest()) {
          return;
        }

        console.error("Failed to fetch user badges:", error);
        setUserBadges([]);
      }
    };

    // Run both fetches in parallel
    fetchDckslapBalance();
    fetchUserBadges();

    return () => {
      isCancelled = true;
    };
  }, [connected, selectedAccount]);

  useEffect(() => {
    const badgeId = userBadges[0];
    if (!badgeId || !accountAddress) {
      setHasRedicks(false);
      return undefined;
    }

    let isCancelled = false;

    const checkHasDicks = async () => {
      try {
        const response = await fetch(
          RADIX_API_GATEWAY_URL + "/state/non-fungible/data",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              resource_address: DCKSLAP_USER_BADGE_RESOURCE_ADDRESS,
              non_fungible_ids: [`#${badgeId}#`],
            }),
          }
        );
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        const fields =
          data.non_fungible_ids[0]?.data?.programmatic_json?.fields;
        if (
          isCancelled ||
          activeAccountAddressRef.current !== accountAddress
        ) {
          return;
        }
        if (!fields) {
          setHasRedicks(false);
          return;
        }
        const has_redicks = fields.find(
          (field) => field.field_name === "has_dicks"
        )?.value;
        setHasRedicks(Boolean(has_redicks));
      } catch (error) {
        if (
          isCancelled ||
          activeAccountAddressRef.current !== accountAddress
        ) {
          return;
        }

        console.error("Failed to check DCKSLAP balance:", error);
        setHasRedicks(false);
      }
    };

    checkHasDicks();

    return () => {
      isCancelled = true;
    };
  }, [accountAddress, userBadges]);

  useEffect(() => {
    if (!connected || !selectedAccount) {
      setDepositCount(0);
      return undefined;
    }

    const requestedAccountAddress = selectedAccount.address;
    let isCancelled = false;

    setDepositCount(0);

    const fetchAllDeposits = async () => {
      let count = 0;
      let cursor = null;

      try {
        do {
          const response = await fetch(
            RADIX_API_GATEWAY_URL + "/stream/transactions",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                cursor,
                order: "asc",
                opt_ins: {
                  balance_changes: true,
                  receipt_output: false,
                },
                limit_per_page: 500,
                manifest_accounts_withdrawn_from_filter: [
                  requestedAccountAddress,
                ],
                transaction_status_filter: "Success",
                manifest_badges_presented_filter: [
                  DCKSLAP_USER_BADGE_RESOURCE_ADDRESS,
                ],
                events_filter: [
                  {
                    event: "Withdrawal",
                    emitter_address: requestedAccountAddress,
                    resource_address: DCKSLAP_RESOURCE_ADDRESS,
                  },
                ],
                affected_global_entities_filter: [DCKSLAP_COMPONENT_ADDRESS],
              }),
            }
          );

          if (!response.ok) {
            throw new Error(
              `Network response was not ok: ${response.statusText}`
            );
          }

          const data = await response.json();

          count += data.items.length;

          cursor = data.next_cursor;
        } while (cursor && !isCancelled);

        if (
          isCancelled ||
          activeAccountAddressRef.current !== requestedAccountAddress
        ) {
          return;
        }

        setDepositCount(count);
      } catch (error) {
        if (
          isCancelled ||
          activeAccountAddressRef.current !== requestedAccountAddress
        ) {
          return;
        }

        console.error("Failed to fetch deposit transactions:", error);
        setDepositCount(0);
      }
    };

    fetchAllDeposits();

    return () => {
      isCancelled = true;
    };
  }, [connected, selectedAccount]);

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS - Transaction logic (unchanged)
  // ─────────────────────────────────────────────────────────────────────────────

  const handleGenerateSlap = async () => {
    if (status !== "ready" || slapsRemaining <= 0 || !connected || !rdt) return;

    const claimAccountAddress = selectedAccount?.address;
    const userBadgeId = userBadges.length > 0 ? userBadges[0] : null;
    if (!claimAccountAddress || !userBadgeId) {
      console.error(
        "Attempted to generate a slap without the required badge (ID: 2)."
      );
      return;
    }

    setStatus("charging");

    const manifest = `
            CALL_METHOD
                Address("${claimAccountAddress}")
                "create_proof_of_non_fungibles"
                Address("${DCKSLAP_USER_BADGE_RESOURCE_ADDRESS}")
                Array<NonFungibleLocalId>(NonFungibleLocalId("#${userBadgeId}#"))
            ;
            POP_FROM_AUTH_ZONE
                Proof("dckuserbadge_proof")
            ;
            CALL_METHOD
                Address("${DCKSLAP_COMPONENT_ADDRESS}")
                "claim"
                Proof("dckuserbadge_proof")
            ;
            CALL_METHOD
                Address("${claimAccountAddress}")
                "deposit_batch"
                Expression("ENTIRE_WORKTOP")
            ;
        `;

    const result = await rdt.walletApi.sendTransaction({
      transactionManifest: manifest,
      version: 1,
    });

    const isClaimAccountStillActive =
      activeAccountAddressRef.current === claimAccountAddress;

    if (result.isErr()) {
      console.error("Transaction error:", result.error);
      if (isClaimAccountStillActive) {
        setStatus("error");
      }
    } else {
      console.log("Transaction success:", result.value);
      const updatedSlapCount = Math.max(
        getStoredSlapState(claimAccountAddress) - 1,
        0
      );

      persistSlapState(claimAccountAddress, updatedSlapCount);
      if (isClaimAccountStillActive) {
        setStatus("ready");
        setSlapsRemaining(updatedSlapCount);
        setTransactionFeedbackVariant("mint");
        setShowTransactionFeedback(true);

        setShowFlash(true);
        setTimeout(() => {
          setShowFlash(false);
          setShowShockwave(true);
          setTimeout(() => {
            setShowShockwave(false);
          }, 1000);
        }, 100);
      }
    }
  };

  const handleBuy = async () => {
    if (!connected || !rdt) return;

    const userBadgeId = userBadges.length > 0 ? userBadges[0] : null;
    if (!userBadgeId) {
      console.error(
        "Attempted to deposit a slap without the required badge (ID: 1)."
      );
      return;
    }

    const manifest = `
            CALL_METHOD
                Address("${selectedAccount.address}")
                "withdraw"
                Address("${REDDICKS_RESSOURCE_ADDRESS}")
                Decimal("20")
            ;
            TAKE_ALL_FROM_WORKTOP
                Address("${REDDICKS_RESSOURCE_ADDRESS}")
                Bucket("reddicks")
            ;
            CALL_METHOD
                Address("${selectedAccount.address}")
                "create_proof_of_non_fungibles"
                Address("${DCKSLAP_USER_BADGE_RESOURCE_ADDRESS}")
                Array<NonFungibleLocalId>(NonFungibleLocalId("#${userBadgeId}#"))
            ;
            POP_FROM_AUTH_ZONE
                Proof("dckuserbadge_proof")
            ;
            CALL_METHOD
                Address("${DCKSLAP_COMPONENT_ADDRESS}")
                "pay_claim"
                Proof("dckuserbadge_proof")
                Bucket("reddicks")
            ;
            CALL_METHOD
                Address("${selectedAccount.address}")
                "deposit_batch"
                Expression("ENTIRE_WORKTOP")
            ;
        `;

    const result = await rdt.walletApi.sendTransaction({
      transactionManifest: manifest,
      version: 1,
    });

    if (result.isErr()) {
      console.error("Transaction error:", result.error);
    } else {
      console.log("Transaction success:", result.value);
    }
  };

  const handleDepositSlap = async () => {
    if (depositStatus !== "ready" || slapsOwned <= 0 || !connected || !rdt)
      return;

    const depositAccountAddress = selectedAccount?.address;
    const userBadgeId = userBadges.length > 0 ? userBadges[0] : null;
    if (!depositAccountAddress || !userBadgeId) {
      console.error(
        "Attempted to deposit a slap without the required badge (ID: 1)."
      );
      return;
    }

    setDepositStatus("charging");

    const manifest = `
            CALL_METHOD
                Address("${depositAccountAddress}")
                "withdraw"
                Address("${DCKSLAP_RESOURCE_ADDRESS}")
                Decimal("1")
            ;
            TAKE_ALL_FROM_WORKTOP
                Address("${DCKSLAP_RESOURCE_ADDRESS}")
                Bucket("dckslap")
            ;
            CALL_METHOD
                Address("${depositAccountAddress}")
                "create_proof_of_non_fungibles"
                Address("${DCKSLAP_USER_BADGE_RESOURCE_ADDRESS}")
                Array<NonFungibleLocalId>(NonFungibleLocalId("#${userBadgeId}#"))
            ;
            POP_FROM_AUTH_ZONE
                Proof("dckuserbadge_proof")
            ;
            CALL_METHOD
                Address("${DCKSLAP_COMPONENT_ADDRESS}")
                "burn"
                Proof("dckuserbadge_proof")
                Bucket("dckslap")
            ;
            CALL_METHOD
                Address("${depositAccountAddress}")
                "deposit_batch"
                Expression("ENTIRE_WORKTOP")
            ;
        `;
    const result = await rdt.walletApi.sendTransaction({
      transactionManifest: manifest,
      version: 1,
    });

    const isDepositAccountStillActive =
      activeAccountAddressRef.current === depositAccountAddress;

    if (result.isErr()) {
      console.error("Deposit transaction error:", result.error);
      if (isDepositAccountStillActive) {
        setDepositStatus("error");
      }
    } else {
      console.log("Deposit transaction success:", result.value);
      if (isDepositAccountStillActive) {
        setDepositStatus("success");
        setSlapsOwned((prev) => prev - 1);

        setTransactionFeedbackVariant("deposit");
        setShowTransactionFeedback(true);

        setDepositFlash(true);
        setTimeout(() => {
          setDepositFlash(false);
          setDepositShockwave(true);
          setTimeout(() => {
            setDepositShockwave(false);
          }, 1000);
        }, 100);

        setTimeout(() => {
          setDepositStatus("ready");
        }, 2000);
        setDepositCount((prev) => prev + 1);
      }
    }
  };

  const handleSendSlap = () => {
    // Navigate to the dedicated Send Slap page
    navigate("/dckslap/sendslap");
  };

  // Show modal when withdraw/deposit conditions aren't met
  const handleWithdrawClick = () => {
    if (!userBadges.length) {
      setModalType('NO_BADGE');
      setShowInsufficientModal(true);
      return;
    }
    if (!hasRedicks) {
      setModalType('NO_REDDICKS');
      setShowInsufficientModal(true);
      return;
    }
    // If conditions met, proceed with withdraw
    handleGenerateSlap();
  };

  const handleDepositClick = () => {
    if (!userBadges.length) {
      setModalType('NO_BADGE');
      setShowInsufficientModal(true);
      return;
    }
    if (slapsOwned <= 0) {
      setModalType('NO_DCKSLAP');
      setShowInsufficientModal(true);
      return;
    }
    // If conditions met, proceed with deposit
    handleDepositSlap();
  };

  const handleCloseModal = () => {
    setShowInsufficientModal(false);
    setModalType(null);
  };


  const handleTryAgain = () => {
    setStatus("ready");
  };


  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER CONTENT - State-based content rendering
  // ─────────────────────────────────────────────────────────────────────────────

  const renderContent = () => {
    switch (status) {
      case "success":
        return (
          <div className="flex flex-col items-center text-center py-8">
            <div className="status-icon-success w-20 h-20 rounded-full flex items-center justify-center mb-6">
              <FaGift className="text-4xl text-success" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-success mb-4">
              Withdrawal SUCCESSFUL!
            </h2>
            <p className="text-muted-foreground mb-8 max-w-md leading-relaxed">
              Your spank has been withdrawn from the bank and is ready in your
              cockpit. Who will be your target?
            </p>
            <Button
              onClick={handleSendSlap}
              size="lg"
              className="gap-2"
              aria-label="Navigate to send your DCKSLAP to another wallet"
            >
              Send Your Slap
              <FaArrowRight className="text-sm" />
            </Button>
          </div>
        );
      case "error":
        return (
          <div className="flex flex-col items-center text-center py-8">
            <div className="status-icon-error w-20 h-20 rounded-full flex items-center justify-center mb-6">
              <FaBolt className="text-4xl text-destructive" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-destructive mb-4 slap-error-title">
              TRANSACTION FAILED
            </h2>
            <p className="text-muted-foreground mb-8 max-w-md leading-relaxed">
              Something went wrong while generating your slap. Please try again.
            </p>
            <Button
              variant="destructive"
              onClick={handleTryAgain}
              size="lg"
              aria-label="Try generating a DCKSLAP again"
            >
              Try Again
            </Button>
          </div>
        );
      case "charging":
      case "ready":
      default:
        return (
          <SlapVault
            withdrawTitle="The Spank Bank"
            depositTitle="The Deposit Box"
            withdrawInstruction={`You have <strong class="text-primary">${slapsRemaining}</strong> of ${DCKSLAP_DAILY_WITHDRAWAL_LIMIT} available to withdraw from the bank today.`}
            depositInstruction={`You have deposited <strong class="text-success">${depositCount}</strong> slaps. Earn a GBOF for every 300 deposited!`}
            slapsRemaining={slapsRemaining}
            slapsOwned={slapsOwned}
            status={status}
            depositStatus={depositStatus}
            onWithdraw={handleWithdrawClick}
            onDeposit={handleDepositClick}
            isWithdrawDisabled={status !== "ready" || slapsRemaining <= 0}
            isDepositDisabled={depositStatus !== "ready"}
          />
        );
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">

      {/* Main Vault Card */}
      <Card
        className={`
          card-gradient relative overflow-hidden
          ${(status === "charging" || depositStatus === "charging") ? "card-charging" : ""}
        `}
      >
        {/* Screen flash effect */}
        {(showFlash || depositFlash) && (
          <div
            className="fixed inset-0 pointer-events-none z-50"
            style={{
              background: "white",
              animation: "flash 0.15s ease-out",
            }}
            aria-hidden="true"
          />
        )}

        {/* Shockwave effect */}
        {(showShockwave || depositShockwave) && (
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none z-10"
            style={{
              width: "50px",
              height: "50px",
              border: "3px solid hsl(0 70% 45%)",
              animation: "shockwave 1s ease-out",
            }}
            aria-hidden="true"
          />
        )}

        <CardContent className="p-6 md:p-10">
          {renderContent()}
        </CardContent>
      </Card>

      {/* Buy More Slaps Section */}
      <Card className="card-buy-more hover:border-primary/40 transition-colors duration-200">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg md:text-xl font-semibold text-primary mb-2">
                Need More Slaps?
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Daaaamn.. you claimed all your slaps already? You can buy more
                here for 20 reddicks each, and keep climbing in the leaderboards!
              </p>
            </div>
            <Button
              onClick={handleBuy}
              size="lg"
              className="shrink-0 gap-2"
              aria-label="Buy more SLAPS for 20 reddicks each"
            >
              <FaCoins />
              Buy More Slaps
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Cards Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Mission Briefing Card */}
        <Card className="card-accent-muted hover:border-primary/30 transition-all duration-200 hover:-translate-y-1">
          <CardHeader>
            <CardTitle className="text-lg text-muted-foreground">
              Your Slap Has Purpose
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              This isn't just fun and games (okay, it mostly is). Every DCKSLAP
              you unleash is a display of fortitude of the Radix network. You're
              not just DCKSlapping; you're hardening the network, helping the
              ecosystem grow erect, and showcasing how you use it. You're taking
              Radix from wet dream to reality.
            </p>
            <Button
              onClick={() => navigate("/dckslap/sendslap")}
              variant="default"
              className="w-full gap-2"
              aria-label="Navigate to Send Slap page to start DCKSLAPPING"
            >
              START DCKSLAPPING
              <FaHandPaper />
            </Button>
            <Button
              onClick={() => navigate("/dckslap/howtoplay")}
              variant="secondary"
              className="w-full gap-2"
              aria-label="Navigate to How to Play page to learn DCKSLAP rules"
            >
              <FaQuestionCircle />
              HOW TO PLAY
            </Button>
          </CardContent>
        </Card>

        {/* What's Next Card */}
        <Card className="card-accent-primary hover:border-primary/30 transition-all duration-200 hover:-translate-y-1">
          <CardHeader>
            <CardTitle className="text-lg text-primary">
              It's Not Binance, it's Slapnance here at DCKBase
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your slap is ready, but the journey has just begun. Climb the ranks,
              forge legendary items, and give someone a DCKSlap!
            </p>
            <Button
              onClick={() => navigate("/dckslap/leaderboard")}
              className="w-full gap-2"
              aria-label="Navigate to Leaderboard page to view rankings"
            >
              <FaTrophy />
              VIEW LEADERBOARDS
            </Button>
            <Button
              onClick={() => navigate("/dckslap/howtoplay")}
              variant="secondary"
              className="w-full gap-2"
              aria-label="Navigate to How to Play page to learn DCKSLAP rules"
            >
              <FaQuestionCircle />
              HOW TO PLAY
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Send Slap - Now uses dedicated page via navigation */}

      {/* Transaction Feedback Animation */}
      <TransactionFeedback
        isVisible={showTransactionFeedback}
        variant={transactionFeedbackVariant}
        onComplete={() => setShowTransactionFeedback(false)}
      />


      {/* Insufficient Modal */}
      {showInsufficientModal && (
        <div className="insufficient-modal-overlay">
          <div
            className="insufficient-modal-backdrop"
            onClick={handleCloseModal}
          />
          <div className="insufficient-modal-content">
            <button
              className="insufficient-modal-close"
              onClick={handleCloseModal}
            >
              <FaTimes />
            </button>
            <div className="insufficient-modal-icon">
              {modalType === 'NO_BADGE' ? <FaLock /> : <FaExclamationTriangle />}
            </div>
            <h3 className="insufficient-modal-title">
              {modalType === 'NO_DCKSLAP' && 'No DCKSLAP Tokens'}
              {modalType === 'NO_BADGE' && 'Badge Required'}
              {modalType === 'NO_REDDICKS' && 'Insufficient RedDicks'}
            </h3>
            <p className="insufficient-modal-message">
              {modalType === 'NO_DCKSLAP' && (
                <>
                  You need at least <strong>1 DCKSLAP</strong> to deposit.
                  <br />
                  Withdraw from the Spank Bank or buy more to get started!
                </>
              )}
              {modalType === 'NO_BADGE' && (
                <>
                  You need a <strong>DCKSLAP Badge</strong> to use the Spank Bank.
                  <br />
                  Hold <strong>15,000 RedDicks</strong> in your wallet and badges are distributed automatically every 24 hours!
                </>
              )}
              {modalType === 'NO_REDDICKS' && (
                <>
                  You need to hold <strong>15K RedDicks</strong> for 24 hours to withdraw slaps.
                  <br />
                  Keep your bag filled to maintain your slapping privileges!
                </>
              )}
            </p>
            <div className="insufficient-modal-actions">
              {modalType === 'NO_DCKSLAP' && (
                <button
                  className="insufficient-modal-btn primary"
                  onClick={handleCloseModal}
                >
                  <span>Got It</span>
                </button>
              )}
              {modalType === 'NO_BADGE' && (
                <button
                  className="insufficient-modal-btn primary"
                  onClick={handleCloseModal}
                >
                  <span>Close</span>
                </button>
              )}
              {modalType === 'NO_REDDICKS' && (
                <button
                  className="insufficient-modal-btn primary"
                  onClick={handleCloseModal}
                >
                  <span>Got It</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SlapGenerator;
